import React, { useMemo, useState } from 'react';

import { useProjectContext, useProjectContextDispatch, reloadProjectAccountingData } from '@contexts/ProjectContext';

import { forceReloadProject } from '@hooks/API/Project/UseProject';
import { forceReloadProjectStatements, useProjectStatements } from '@hooks/API/Project/UseProjectStatements';

import { Loading } from '@components/Gui/Loading';
import FamiliesSelector from '@components/Transactions/FamiliesSelector';
import TransactionCreateDrawer from '@components/Transactions/TransactionsDrawer/TransactionCreateDrawer';
import TransactionDetailDrawer from '@components/Transactions/TransactionsDrawer/TransactionDetailDrawer';
import TransactionsStatusLegend from '@components/Transactions/TransactionsStatusLegend';

import ProjectGroupedStatementsBillingInfo from './ProjectGroupedStatementsBillingInfo.js';
import ProjectGroupedStatementsGrandTotal from './ProjectGroupedStatementsGrandTotal.js';
import { ProjectTransactionListContainer } from './ProjectTransactionsUI';

const ProjectGroupedStatements = ({ statementType, readOnly, isExpanded }) => {
    const { projectId, projectData: project } = useProjectContext();
    const projectDispatch = useProjectContextDispatch();
    const [showTransactionDetail, setShowTransactionDetail] = useState(false);
    const [transaction, setTransaction] = useState();
    const [transactionFamily, setTransactionFamily] = useState();
    const [showTransactionDrawer, setShowTransactionDrawer] = useState(false);
    const { data: statements /*isFetching: loading*/ } = useProjectStatements(projectId, statementType);
    const [selectedFamilies, setSelectedFamilies] = useState([]);

    const usedFamilies = useMemo(() => {
        let families = [];
        if (statements && statements.length) {
            //find families used in statements
            for (const contactstatement of statements) {
                for (const billinginfo of contactstatement.BillingInfos) {
                    for (const transaction of billinginfo.Transactions) {
                        if (transaction.Family && !families.includes(transaction.Family)) {
                            families.push(transaction.Family);
                        }
                    }
                }
            }
        }
        return families;
    }, [statements]);

    const filteredStatements = useMemo(() => {
        let filteredStatements = [];
        if (statements && statements.length) {
            if (selectedFamilies.length) {
                for (const contactstatement of statements) {
                    const filteredbillinginfo = contactstatement.BillingInfos.map((bi) => {
                        if (bi.Transactions && bi.Transactions.length) {
                            const filteredtransactions = bi.Transactions.filter((transaction) =>
                                selectedFamilies.includes(transaction.Family)
                            );
                            return {
                                ...bi,
                                Transactions: filteredtransactions,
                            };
                        } else {
                            return bi;
                        }
                    }).filter((bi) => bi.Transactions && bi.Transactions.length);
                    filteredStatements.push({
                        ...contactstatement,
                        BillingInfos: filteredbillinginfo,
                    });
                }
                //remove contactstatement without a billinginfo with statements
                filteredStatements = filteredStatements.filter(
                    (contactstatement) => contactstatement.BillingInfos && contactstatement.BillingInfos.length
                );
            } else {
                filteredStatements = statements;
            }
        }
        return filteredStatements;
    }, [statements, selectedFamilies]);

    const visibleBillingInfosCount = useMemo(() => {
        return filteredStatements.reduce((count, contact) => count + (contact.BillingInfos?.length || 0), 0);
    }, [filteredStatements]);

    const handleEditTransaction = (transaction) => {
        setTransaction(transaction);
        setShowTransactionDetail(true);
    };

    const handleCloseTransactionDetail = () => {
        setTransaction();
        setShowTransactionDetail(false);
    };

    const handleCloseTransactionDrawer = () => {
        setTransactionFamily();
        setShowTransactionDrawer(false);
    };

    const handleSelectFamily = (family) => {
        if (selectedFamilies.includes(family)) {
            setSelectedFamilies(selectedFamilies.filter((f) => f !== family));
        } else {
            setSelectedFamilies([...selectedFamilies, family]);
        }
    };

    return filteredStatements ? (
        <ProjectTransactionListContainer>
            <FamiliesSelector
                families={usedFamilies}
                selectedFamilies={selectedFamilies}
                onSelectFamily={handleSelectFamily}
                onSelectAll={() => setSelectedFamilies([])}
            />
            {filteredStatements.map((contact) => (
                <div className="subject-container" key={`contact-${contact.ContactID}`}>
                    {contact.BillingInfos.length > 1 && <h2>{contact.Name}</h2>}
                    {contact.BillingInfos.map((bi) => (
                        <ProjectGroupedStatementsBillingInfo
                            contactID={contact.ContactID}
                            contactName={contact.Name}
                            statementType={statementType}
                            isSingleBillingInfo={contact.BillingInfos.length === 1}
                            key={bi.ID}
                            billingInfo={bi}
                            project={project}
                            onEditTransaction={handleEditTransaction}
                            readOnly={readOnly}
                            isExpanded={isExpanded}
                            showTotals
                            onReloadProject={async () => {
                                forceReloadProjectStatements(project.ID);
                                const updatedProject = await forceReloadProject(project.ID);
                                projectDispatch({ type: 'UpdateProjectData', projectData: updatedProject });
                                await reloadProjectAccountingData(project.ID, projectDispatch);
                            }}
                        />
                    ))}
                </div>
            ))}
            {visibleBillingInfosCount > 1 && <ProjectGroupedStatementsGrandTotal contacts={filteredStatements} />}
            <div className="transactions-status-legend-container">
                <TransactionsStatusLegend />
            </div>
            {showTransactionDrawer && (
                <TransactionCreateDrawer
                    projectId={project.ID}
                    modelSelectionEnabled
                    transactionFamily={transactionFamily}
                    onClose={handleCloseTransactionDrawer}
                    onTransactionCreatedOrUpdated={async () => {
                        forceReloadProjectStatements(project.ID);
                        const updatedProject = await forceReloadProject(project.ID);
                        projectDispatch({ type: 'UpdateProjectData', projectData: updatedProject });
                        await reloadProjectAccountingData(project.ID, projectDispatch);
                    }}
                />
            )}
            {showTransactionDetail && (
                <TransactionDetailDrawer
                    hideProject
                    transaction={transaction}
                    projectId={project.ID}
                    onClose={handleCloseTransactionDetail}
                    onUpdateTransaction={async () => {
                        forceReloadProjectStatements(project.ID);
                        const updatedProject = await forceReloadProject(project.ID);
                        projectDispatch({ type: 'UpdateProjectData', projectData: updatedProject });
                        await reloadProjectAccountingData(project.ID, projectDispatch);
                    }}
                    onTransactionRemoved={async () => {
                        setTransaction();
                        setShowTransactionDetail(false);
                        forceReloadProjectStatements(project.ID);
                        const updatedProject = await forceReloadProject(project.ID);
                        projectDispatch({ type: 'UpdateProjectData', projectData: updatedProject });
                        await reloadProjectAccountingData(project.ID, projectDispatch);
                    }}
                />
            )}
        </ProjectTransactionListContainer>
    ) : (
        <Loading textBlack />
    );
};

export default ProjectGroupedStatements;
